import { create } from 'zustand'
import { videoService } from '../services/videoService'

export interface ActiveUpload {
  videoId: string
  progress: number // 0-100
  phase: 'uploading' | 'finalizing' | 'error'
  xhr: XMLHttpRequest | null
  fileName: string
  title: string
  category: string | null
  tags: string[] | null
  errorMessage?: string
}

interface UploadStore {
  // State
  activeUploads: Record<string, ActiveUpload>

  // Actions
  startUpload: (
    videoId: string,
    xhr: XMLHttpRequest,
    fileName: string,
    title: string,
    category: string | null,
    tags: string[] | null
  ) => void
  updateProgress: (videoId: string, progress: number) => void
  setFinalizing: (videoId: string) => void
  completeUpload: (videoId: string) => Promise<void>
  cancelUpload: (videoId: string) => void
  setUploadError: (videoId: string, errorMessage: string) => void
  removeUpload: (videoId: string) => void
  getUpload: (videoId: string) => ActiveUpload | undefined
  hasActiveUploads: () => boolean
}

export const useUploadStore = create<UploadStore>()((set, get) => ({
  activeUploads: {},

  startUpload: (videoId, xhr, fileName, title, category, tags) => {
    set((state) => ({
      activeUploads: {
        ...state.activeUploads,
        [videoId]: {
          videoId,
          progress: 0,
          phase: 'uploading',
          xhr,
          fileName,
          title,
          category,
          tags,
        },
      },
    }))
  },

  updateProgress: (videoId, progress) => {
    set((state) => {
      const upload = state.activeUploads[videoId]
      if (!upload) return state

      return {
        activeUploads: {
          ...state.activeUploads,
          [videoId]: {
            ...upload,
            progress,
          },
        },
      }
    })
  },

  setFinalizing: (videoId) => {
    set((state) => {
      const upload = state.activeUploads[videoId]
      if (!upload) return state

      return {
        activeUploads: {
          ...state.activeUploads,
          [videoId]: {
            ...upload,
            phase: 'finalizing',
            progress: 100,
          },
        },
      }
    })
  },

  completeUpload: async (videoId) => {
    const upload = get().activeUploads[videoId]
    if (!upload) return

    // Update video metadata in the database
    try {
      await videoService.updateVideo(videoId, {
        category: upload.category,
        tags: upload.tags,
      })
    } catch (error) {
      console.error('Failed to update video metadata:', error)
      // Don't fail the upload for metadata errors - video is uploaded
    }

    // Remove from active uploads
    set((state) => {
      const { [videoId]: _, ...remaining } = state.activeUploads
      return { activeUploads: remaining }
    })
  },

  cancelUpload: (videoId) => {
    const upload = get().activeUploads[videoId]
    if (!upload) return

    // Abort the XHR if still in progress
    if (upload.xhr && upload.phase === 'uploading') {
      upload.xhr.abort()
    }

    // Remove from active uploads
    set((state) => {
      const { [videoId]: _, ...remaining } = state.activeUploads
      return { activeUploads: remaining }
    })

    // Delete the video record from database (it was created in 'uploading' state)
    videoService.deleteVideo(videoId).catch((error) => {
      console.error('Failed to delete cancelled video:', error)
    })
  },

  setUploadError: (videoId, errorMessage) => {
    set((state) => {
      const upload = state.activeUploads[videoId]
      if (!upload) return state

      return {
        activeUploads: {
          ...state.activeUploads,
          [videoId]: {
            ...upload,
            phase: 'error',
            errorMessage,
            xhr: null,
          },
        },
      }
    })
  },

  removeUpload: (videoId) => {
    set((state) => {
      const { [videoId]: _, ...remaining } = state.activeUploads
      return { activeUploads: remaining }
    })
  },

  getUpload: (videoId) => {
    return get().activeUploads[videoId]
  },

  hasActiveUploads: () => {
    const uploads = get().activeUploads
    return Object.values(uploads).some(
      (upload) => upload.phase === 'uploading' || upload.phase === 'finalizing'
    )
  },
}))
